import { useRef, useState } from "react";
import { ArrowUpCircle, CheckCircle, Clock, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PixPaymentDialog } from "@/components/checkout/PixPaymentDialog";
import {
  CreditCardForm,
  isCardDataComplete,
  toCheckoutCreditCard,
  type CardFormState,
} from "@/components/checkout/CreditCardForm";
import {
  CardHolderForm,
  EMPTY_CARD_HOLDER,
  isCardHolderComplete,
  isCardHolderPhoneValid,
  toCreditCardHolder,
  type CardHolderFormState,
} from "@/components/checkout/CardHolderForm";
import { PaymentMethodSelector } from "@/components/checkout/PaymentMethodSelector";
import {
  fetchUpgradePixQr,
  initiatePlanUpgrade,
} from "@/services/subscription.service";
import { toast } from "@/hooks/use-toast";
import type {
  CheckoutPaymentResponse,
  EligiblePlanItem,
} from "@/backend-sdk/client/types.gen";

const EMPTY_CARD: CardFormState = {
  holderName: "",
  number: "",
  expiryMonth: "",
  expiryYear: "",
  ccv: "",
};

// PIX: o QR pode demorar para ser gerado pelo gateway; consultar até
// obter o código (2s por tentativa, máximo de 15 tentativas).
const PIX_QR_POLL_INTERVAL_MS = 2000;
const PIX_QR_MAX_ATTEMPTS = 15;

const formatBRL = (value: string | number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value));

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("pt-BR");

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlanName?: string | null;
  selected: EligiblePlanItem | null;
  onSuccess?: () => void;
}

export const UpgradeDialog = ({
  open,
  onOpenChange,
  currentPlanName,
  selected,
  onSuccess,
}: UpgradeDialogProps) => {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "monthly",
  );
  const [paymentMethod, setPaymentMethod] = useState<"pix" | "credit_card">(
    "pix",
  );
  const [cardData, setCardData] = useState<CardFormState>(EMPTY_CARD);
  const [cardHolder, setCardHolder] =
    useState<CardHolderFormState>(EMPTY_CARD_HOLDER);
  const [submitting, setSubmitting] = useState(false);
  const [checkout, setCheckout] = useState<CheckoutPaymentResponse | null>(
    null,
  );
  const [pixOpen, setPixOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [success, setSuccess] = useState(false);
  const cancelledRef = useRef(false);

  const reset = () => {
    cancelledRef.current = true;
    setBillingCycle("monthly");
    setPaymentMethod("pix");
    setCardData(EMPTY_CARD);
    setCardHolder(EMPTY_CARD_HOLDER);
    setSubmitting(false);
    setCheckout(null);
    setPixOpen(false);
    setPending(false);
    setSuccess(false);
  };

  const close = () => {
    const wasSuccess = success;
    reset();
    onOpenChange(false);
    if (wasSuccess) onSuccess?.();
  };

  /** Aguarda o QR PIX ficar disponível, abrindo o diálogo assim que chegar. */
  const waitForPixQr = async (invoiceId: string) => {
    for (let attempt = 0; attempt < PIX_QR_MAX_ATTEMPTS; attempt++) {
      if (cancelledRef.current) return;
      await new Promise((resolve) =>
        setTimeout(resolve, PIX_QR_POLL_INTERVAL_MS),
      );
      if (cancelledRef.current) return;
      try {
        const data = await fetchUpgradePixQr(invoiceId);
        if (cancelledRef.current) return;
        if (data.pix_copy_paste) {
          setCheckout((prev) =>
            prev ? { ...prev, pix_copy_paste: data.pix_copy_paste } : prev,
          );
          setPixOpen(true);
          return;
        }
      } catch {
        // QR ainda indisponível (ou erro transitório); continua tentando.
      }
    }
    if (!cancelledRef.current) {
      setPending(true);
    }
  };

  const handleConfirm = async () => {
    if (!selected || submitting) return;
    if (paymentMethod === "credit_card" && !isCardDataComplete(cardData)) {
      toast({
        title: "Dados do cartão incompletos",
        description: "Preencha todos os campos do cartão de crédito",
        variant: "destructive",
      });
      return;
    }
    if (paymentMethod === "credit_card" && !isCardHolderComplete(cardHolder)) {
      toast({
        title: "Endereço do titular incompleto",
        description: "Informe o CEP e o número do endereço do titular do cartão",
        variant: "destructive",
      });
      return;
    }
    if (paymentMethod === "credit_card" && !isCardHolderPhoneValid(cardHolder)) {
      toast({
        title: "Telefone do titular inválido",
        description:
          "Informe o telefone com DDD, ou deixe em branco para usar o do seu cadastro",
        variant: "destructive",
      });
      return;
    }

    cancelledRef.current = false;
    setSubmitting(true);
    try {
      const res = await initiatePlanUpgrade({
        plan_id: selected.plan.id,
        billing_cycle: billingCycle,
        payment_method: paymentMethod,
        credit_card:
          paymentMethod === "credit_card"
            ? toCheckoutCreditCard(cardData)
            : undefined,
        credit_card_holder:
          paymentMethod === "credit_card"
            ? toCreditCardHolder(cardHolder)
            : undefined,
      });
      if (cancelledRef.current) return;
      setCheckout(res);

      if (res.billing_type === "ZERO_VALUE" || res.status === "CONFIRMED") {
        setSuccess(true);
      } else if (res.pix_copy_paste) {
        setPixOpen(true);
      } else if (res.billing_type === "PIX" && res.invoice_id) {
        await waitForPixQr(res.invoice_id);
      } else {
        setPending(true);
      }
    } catch (error: unknown) {
      if (cancelledRef.current) return;
      const message =
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string"
          ? error.message
          : "Não foi possível concluir o upgrade. Tente novamente.";
      toast({
        title: "Erro no upgrade",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const plan = selected?.plan;
  const preview = selected?.proration_preview;
  const isYearly = billingCycle === "yearly";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="sm:max-w-md">
        {success ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Plano atualizado com sucesso!
              </DialogTitle>
              <DialogDescription>
                {preview
                  ? `A próxima cobrança será de ${formatBRL(
                      preview.next_renewal_amount,
                    )} a partir de ${formatDate(preview.next_renewal_date)}.`
                  : "A próxima cobrança será feita conforme o novo plano."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={close}>Fechar</Button>
            </DialogFooter>
          </>
        ) : pending ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Pagamento em análise
              </DialogTitle>
              <DialogDescription>
                Seu plano será atualizado assim que o pagamento for confirmado
                pelo gateway. Isso pode levar alguns minutos.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={close}>Fechar</Button>
            </DialogFooter>
          </>
        ) : !plan ? (
          <>
            <DialogHeader>
              <DialogTitle>Alterar Plano</DialogTitle>
              <DialogDescription>
                Nenhum plano elegível para upgrade no momento.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Fechar
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowUpCircle className="h-5 w-5 text-green-500" />
                Fazer Upgrade
              </DialogTitle>
              <DialogDescription>
                {currentPlanName ? `Do plano ${currentPlanName} para` : "Para"}{" "}
                o plano <strong>{plan.name}</strong> (
                {formatBRL(isYearly ? plan.price_yearly : plan.price_monthly)}
                {isYearly ? "/ano" : "/mês"}).
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {preview && (
                <div className="p-4 border rounded-lg bg-muted/50 space-y-1">
                  <p className="text-sm">
                    <strong>{formatBRL(preview.prorated_difference)}</strong>{" "}
                    agora (proporcional aos {preview.days_remaining_in_period}{" "}
                    dias restantes do ciclo)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Depois: {formatBRL(preview.next_renewal_amount)}/mês a
                    partir de {formatDate(preview.next_renewal_date)}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Ciclo de cobrança</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={isYearly ? "outline" : "default"}
                    onClick={() => setBillingCycle("monthly")}
                  >
                    Mensal
                  </Button>
                  <Button
                    type="button"
                    variant={isYearly ? "default" : "outline"}
                    onClick={() => setBillingCycle("yearly")}
                  >
                    Anual
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Forma de pagamento</Label>
                <PaymentMethodSelector
                  value={paymentMethod}
                  onChange={setPaymentMethod}
                />
              </div>

              {paymentMethod === "credit_card" && (
                <>
                  <CreditCardForm value={cardData} onChange={setCardData} />
                  <CardHolderForm value={cardHolder} onChange={setCardHolder} />
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={submitting}>
                Cancelar
              </Button>
              <Button onClick={handleConfirm} disabled={submitting}>
                {submitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {submitting ? "Processando..." : "Confirmar Upgrade"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>

      {checkout && (
        <PixPaymentDialog
          open={pixOpen}
          onOpenChange={setPixOpen}
          copyPaste={checkout.pix_copy_paste}
          dueDate={checkout.due_date}
          gatewayPaymentId={checkout.gateway_payment_id}
          onPaid={() => {
            setPixOpen(false);
            setSuccess(true);
          }}
        />
      )}
    </Dialog>
  );
};
